import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { MessageThreadTitle } from "src/core/constants/folder";
import { Status } from "src/core/constants/folders";
import { Roles } from "src/core/constants/roles";
import { useFolder } from "src/core/contexts/FolderContext";
import { useUser } from "src/core/contexts/UserContext";
import {
  EditMessageThreadFormValues,
  editMessageThreadSchema,
} from "src/core/forms/folder/editMessageThread.schema";
import {
  SendMessageThreadFormValues,
  sendMessageThreadSchema,
} from "src/core/forms/folder/sendMessageThread.schema";
import {
  FolderFileQuery,
  useAddMessageMutation,
  useEditMessageMutation,
} from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { usePermissions } from "src/core/hooks/usePermissions";

export const useFolderMessageThreadModal = () => {
  const form = useForm<SendMessageThreadFormValues>({
    resolver: zodResolver(sendMessageThreadSchema(t)),
    defaultValues: {
      content: "",
    },
  });
  const formEditing = useForm<EditMessageThreadFormValues>({
    resolver: zodResolver(editMessageThreadSchema()),
    defaultValues: {
      content: "",
    },
  });
  const { folderFile, refetch } = useFolder();
  const [addMessage] = useAddMessageMutation();
  const [editMessage] = useEditMessageMutation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEditing, setIsLoadingEditing] = useState(false);
  const [messageThread, setMessageThread] = useState<
    NonNullable<FolderFileQuery["folderFile"]>["messageThreads"][0] | undefined
  >(undefined);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const { user } = useUser();
  const { hasRole } = usePermissions();

  const isEditable =
    ([Status.DRAFT, Status.VALIDATION_PENDING, Status.VALIDATION_PENDING_HR].includes(
      folderFile.folderFile?.workflowStatus?.state?.code as Status
    ) ??
      false) &&
    !hasRole(Roles.AUDITEUR);

  useEffect(() => {
    setMessageThread(
      folderFile?.folderFile?.messageThreads.find((mt) => mt.title === MessageThreadTitle.COMMENTS)
    );
  }, [folderFile]);

  const onSubmit = useCallback(
    async (data: SendMessageThreadFormValues) => {
      const messageThreadId = folderFile?.folderFile?.messageThreads.find(
        (mt) => mt.title === MessageThreadTitle.COMMENTS
      )?.id;
      if (messageThreadId === undefined) {
        return;
      }
      try {
        setIsLoading(true);
        await addMessage({
          variables: {
            messageThreadId,
            content: data.content,
          },
        });
        await refetch();
        toast({
          description: t("messageThread.success"),
        });
        form.reset();
      } catch (error) {
        toast({
          description: t("messageThread.fail"),
          variant: "destructive",
        });
        console.error("Erreur lors ajout du commentaire:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [folderFile, addMessage, toast, form, refetch]
  );
  const handleEditMessage = useCallback(
    (
      message: NonNullable<FolderFileQuery["folderFile"]>["messageThreads"][0]["messages"][0] | null
    ) => {
      if (message === null) {
        setEditingMessageId(null);
        formEditing.reset();
        return;
      }
      setEditingMessageId(message.id);
      formEditing.reset({
        content: message.content,
      });
    },
    [formEditing]
  );

  const onSubmitEditing = useCallback(
    async (data: EditMessageThreadFormValues) => {
      if (editingMessageId === null || data.content === "") {
        handleEditMessage(null);
        return;
      }
      try {
        setIsLoadingEditing(true);
        await editMessage({
          variables: {
            messageId: editingMessageId,
            content: data.content,
          },
        });
        await refetch();
        toast({
          description: t("messageThread.updatedSuccess"),
        });
      } catch (error) {
        toast({
          description: t("messageThread.fail"),
          variant: "destructive",
        });
        console.error("Erreur lors update du commentaire:", error);
      } finally {
        setIsLoadingEditing(false);
        handleEditMessage(null);
      }
    },
    [editMessage, toast, refetch, editingMessageId, handleEditMessage]
  );

  const lastUserMessageId = (() => {
    if (!messageThread?.messages || !user?.id) return null;
    const allMessages = messageThread.messages
      .filter((message) => message?.adminStatus?.creation)
      .sort(
        (a, b) =>
          new Date(b.adminStatus.creation).getTime() - new Date(a.adminStatus.creation).getTime()
      );

    if (allMessages.length === 0) return null;

    const mostRecentMessage = allMessages[0];
    if (mostRecentMessage?.adminStatus?.author?.id !== user.id) {
      return null;
    }
    return mostRecentMessage.id;
  })();

  return {
    form,
    onSubmit,
    isLoading,
    messageThread,
    user,
    isEditable,
    lastUserMessageId,
    editingMessageId,
    handleEditMessage,
    formEditing,
    onSubmitEditing,
    isLoadingEditing,
  };
};
